/**
 * Contextual help content for every portal page.
 * Used by the floating help bubble and inline section hints.
 */

import { TOUR_IDS } from './portalTourSteps';

export const HELP_VERSION = 'v2';

export const HELP_CHANGELOG = {
  v2: 'Navigation is now organised around the beta tenant workflow: dashboard, people, care delivery, finance, compliance, and admin.',
};

const glossaryCommon = [
  {
    term: 'Billing client',
    definition:
      'The organisation (council, agency, etc.) you invoice. Set on each shift — not the same as the care home where work happens.',
  },
  {
    term: 'Care location',
    definition: 'The home or site where the worker delivers care. Used for GPS clock-in and rota display.',
  },
  {
    term: 'Payable hours',
    definition: 'Hours between approved clock-in and clock-out. Used for worker pay on timesheets and payroll.',
  },
  {
    term: 'Scheduled hours',
    definition: 'Hours on the shift rota (start to end). Client invoices bill scheduled hours, not clock times.',
  },
];

function page(def) {
  return def;
}

export const PORTAL_PAGE_HELP = {
  dashboard: page({
    title: 'Dashboard',
    tagline: 'Your daily control tower for safe, compliant, well-led care',
    overview:
      'This is the single place that tells you, at a glance, whether your organisation is safe to operate today and where attention is needed. It surfaces live counts for staff readiness, recruitment pipeline, expiring compliance items, open tasks, and financial pipeline status — all with direct links to act. Everything here is designed to support CQC Well-led, Regulation 20 Duty of Candour, and safe staffing decisions. No one should ever have to hunt for the state of the organisation.',
    whenToUse:
      'Every morning as the first thing you open. Before any inspection or external review. When a serious incident or complaint comes in. At the end of the week for governance. Any time you need to answer “is it safe for our workers to go out and for our clients to receive care today?”',
    quickSteps: [
      'Start with the Today\'s Work Queue. It translates dashboard numbers into plain-English actions: what is wrong, why it matters, and which button to press.',
      'Scan the “Needs attention” / alerts area first — these are the items that could prevent a worker from being deployed or create a compliance gap.',
      'Review the main stat cards for overall health (staff ready, expiring items, open shifts, financial close status). Click any card to go straight to the source of truth.',
      'Open the task queue and work top-down. Most tasks link directly to the person or record that needs action.',
      'Check the recent activity or pipeline summaries for recruitment and service users so nothing falls through the cracks.',
    ],
    sections: [
      {
        id: 'work-queue',
        title: 'Today\'s Work Queue',
        what: 'A plain-English control centre for the day. It groups the highest-risk areas: workers blocked from work, applicants waiting for review, documents ready to approve, expired or blocking training, governance alerts, and business follow-up.',
        doThis: 'Open any card with a number above zero. Read the reason, press the action button, and complete the work in the source page rather than trying to work around the warning.',
        mistakes: 'Do not approve evidence, training, DBS, right-to-work, or recruitment readiness just to clear the dashboard. If the original document has not been checked, the evidence is unclear, or a manager decision is needed, return it or escalate instead.',
      },
      {
        id: 'stats',
        title: 'Summary cards — the organisation’s vital signs',
        what: 'Live, role-filtered counts: active/ready staff, applicants in pipeline, documents or training about to expire, open shifts, timesheets awaiting processing, invoices, and any high-level compliance or financial signals. Colours (green/amber/red) reflect real risk to deployment or regulatory position.',
        doThis: 'Treat amber/red as “act now”. Click through — the card should take you to the filtered list or profile where you can resolve it. Use this as your daily briefing before you speak to any team member or client.',
        mistakes: 'Treating the numbers as “just data”. Each count has a legal or safety implication (e.g. an expired DBS means that worker must not be on shift; an unprocessed timesheet affects both pay accuracy and client billing transparency).',
      },
      {
        id: 'tasks',
        title: 'Actionable tasks & alerts',
        what: 'System-generated work items (document verification needed, training due, supervision overdue, recruitment gates not met, attendance anomalies, etc.). These are the things the organisation is relying on you (or your team) to close.',
        doThis: 'Work from the top. Complete or delegate and mark done. Most tasks have a direct link to the exact employee/profile/record. If something keeps reappearing, it usually points to a process or data quality issue.',
        mistakes: 'Assuming “someone else will do it” or that the system will auto-clear items. Most require a human decision or upload that only you or the right role can perform.',
      },
      {
        id: 'alerts',
        title: 'Expiry, compliance & financial pipeline alerts',
        what: 'Proactive warnings: DBS/training/RTW about to expire, recurring compliance items due, approved attendance waiting for timesheet, locked timesheets ready for payroll, invoices that can now be raised, etc. These are the items that protect both your workers and the people you support.',
        doThis: 'Open the linked pages (Employees, Training, Shifts, Timesheets, Invoices, Compliance Centre) and action them. The presence of these alerts on the dashboard is itself evidence of a functioning governance system.',
        mistakes: 'Dismissing alerts because “we know about it”. Regulators and families expect timely action, not just awareness.',
      },
    ],
    workflows: [
      { id: 'overview', label: 'Full portal tour', tourId: TOUR_IDS.overview, description: 'Three-minute walkthrough of every menu area and what “good” looks like for a Well-led organisation.' },
      { id: 'morning-brief', label: 'Typical morning governance routine', description: 'Dashboard → top 3 alerts → Employees or Compliance Centre → Shifts for today’s cover.' },
    ],
    glossary: [
      ...glossaryCommon,
      {
        term: 'Work readiness / canonical readiness',
        definition: 'A computed status (green/amber/red) that combines current DBS, mandatory training, right-to-work, key policy acknowledgements, and any blocks. Green = safe to deploy. This is the single most important number on the dashboard for safe staffing.',
      },
      {
        term: 'Financially closed',
        definition: 'An attendance record that has been approved, put on a locked timesheet, processed through payroll (worker paid), and invoiced to the client. The new badges on Shifts and Worker “My Earnings” surface exactly where each record sits in this chain.',
      },
    ],
    relatedLinks: [
      { label: 'Employees — the people and their compliance truth', href: '/portal/employees' },
      { label: 'Compliance Centre — incidents, feedback, policies, CQC evidence', href: '/portal/compliance-centre' },
      { label: 'Shifts — today’s cover and the financial pipeline', href: '/portal/shifts' },
      { label: 'Organisations — the clients who pay us and what we charge them', href: '/portal/organisations' },
    ],
    tips: [
      'The dashboard is deliberately high-signal, low-noise. If you see a lot of red/amber, that is the system doing its job — telling you where to focus so that no worker is put at risk and no family receives sub-standard care.',
      'Everything on this page should be explainable to a CQC inspector or a family member in plain English. If a number surprises you, click through and understand why.',
      'Use the “Full tour” button in the help bubble regularly when onboarding new managers — it is the fastest way to internalise what good governance looks like in this system.',
    ],
  }),

  employees: page({
    title: 'Employees',
    tagline: 'Active staff records and compliance',
    overview:
      'The single source of truth for everyone who works for you. Every profile contains the full picture: right-to-work, DBS, mandatory training, contracts, pay rates, supervision & appraisal history, and a complete audit trail. This page is central to CQC Well-led evidence and safe staffing decisions.',
    whenToUse:
      'Use this page every day for HR and compliance — before rostering anyone, during audits, when reviewing expiring documents, for supervision planning, and whenever you need to confirm a worker is fully cleared to deliver care. Never assign a shift from here if the readiness indicators are red or amber.',
    quickSteps: [
      'Search or filter (by name, role, status, or compliance flag) to find the person quickly.',
      'Click a row to open the full profile. Work through the tabs in order: start with the compliance overview, then Documents, Training, then Shifts & Earnings for operational context.',
      'Only use “Add employee” for true direct hires. Almost everyone should enter via Recruitment so all pre-employment gates (references, DBS, RTW, health) are properly recorded and auditable.',
    ],
    sections: [
      {
        id: 'list',
        title: 'Employee list & readiness',
        what: 'Live list of active staff with clear visual indicators for DBS, training, right-to-work, and overall work-readiness. Red/amber means the person should not be on shift until resolved.',
        doThis: 'Use the filters heavily. Click any row to drill into the full record. The list updates in real time as documents are verified or training is completed.',
        mistakes: 'Rostering someone who shows as non-compliant in this list — the system will usually block the assignment, but you should never get that far.',
      },
      {
        id: 'profile',
        title: 'Employee profile (the full record)',
        what: 'One place for everything: personal details, employment history, pay rate, all compliance documents, training matrix, signed contracts, supervision/appraisal notes, and the complete shift & earnings history.',
        doThis:
          'When onboarding or doing a compliance review, go left-to-right through the tabs. Documents + Training are the most frequently used. Use the Shifts tab and Earnings summary to answer pay or timesheet queries without going to the finance pages. For CQC recruitment-file audits, use Audit pack PDF on the Person File hero — it summarises application, gaps, references, interview method, RTW/DBS/ID/PoA verification, health, and contract status in one download.',
        mistakes: 'Approving or rostering a worker whose profile still has outstanding items — always check the big readiness banner at the top of the profile first.',
      },
      {
        id: 'pay',
        title: 'Pay details (inside the profile)',
        what: 'The worker’s contractual hourly rate (what you pay them). This is separate from the rate you charge the client (set on the Organisations page under Billing rates).',
        doThis: 'Set or update the pay rate when you agree a new rate with the worker or after a review. This feeds directly into timesheets and payroll.',
        mistakes: 'Confusing the worker’s pay rate with the client charge rate — they live in two different places for very good audit reasons.',
      },
    ],
    workflows: [
      { id: 'overview', label: 'Portal tour — People section', tourId: TOUR_IDS.overview },
      { id: 'onboarding', label: 'Typical new starter journey', description: 'Recruitment → profile created → documents & training completed → ready for shifts.' },
    ],
    glossary: [
      ...glossaryCommon,
      {
        term: 'Work readiness',
        definition: 'Green means the worker has current DBS, all mandatory training, right-to-work, and signed key documents. Amber/red = do not roster until resolved.',
      },
    ],
    relatedLinks: [
      { label: 'Recruitment (how most people arrive here)', href: '/portal/recruitment' },
      { label: 'Training catalogue & records', href: '/portal/training' },
      { label: 'Shifts & attendance (where they actually work)', href: '/portal/shifts' },
      { label: 'Organisations – set what clients pay (charge rates)', href: '/portal/organisations' },
    ],
    tips: [
      'Look for the coloured readiness dots and the big banner at the top of any profile — they tell you in one glance whether the person can work today.',
      'The audit history tab at the bottom of the profile is gold during inspections — everything is timestamped and attributed.',
      'If you are ever unsure about pay or a timesheet query, open the worker’s profile and go to the Shifts or Earnings section first — it is usually faster and clearer than the finance pages.',
    ],
  }),

  recruitment: page({
    title: 'Recruitment',
    tagline: 'Applicants through to ready-to-start',
    overview:
      'New hires enter here before they become employees. Track applications, interviews, references, right-to-work, and onboarding gates.',
    whenToUse: 'When someone applies, through interview, offer, and until they appear as an active employee.',
    quickSteps: [
      'Open the pipeline board or list view.',
      'Move applicants through stages; complete forms and document requests.',
      'Record interviews with method (phone/video/in-person), interviewer, and optional video link — these appear on the interview PDF.',
      'Verify identity and proof-of-address via view-then-stamp; RTW/DBS via the guided online-check dialog.',
      'Download Audit pack PDF from the applicant Person File before a recruitment-file audit.',
      'Promote to employee when recruitment is approved — they then appear under Employees.',
    ],
    sections: [
      {
        id: 'pipeline',
        title: 'Pipeline stages',
        what: 'Visual stages from application to ready for work.',
        doThis: 'Only advance when gates are met (DBS, references, interview, etc.). References show Satisfactory only when verified with no unresolved mismatch.',
        mistakes: 'Skipping reference or RTW checks — inspection risk.',
      },
    ],
    relatedLinks: [{ label: 'Employees', href: '/portal/employees' }],
  }),

  'service-users': page({
    title: 'Service users',
    tagline: 'People receiving care',
    overview:
      'Individuals you support. Link them to care plans, locations, shifts, and feedback for CQC traceability.',
    whenToUse: 'Setting up new packages of care, domiciliary visits, or linking shifts to a named person.',
    quickSteps: [
      'Create or open a service user profile.',
      'Add care plan and location details.',
      'When creating shifts, choose “service user visit” shift type to link work.',
    ],
    sections: [
      {
        id: 'profiles',
        title: 'Service user profiles',
        what: 'Demographics, care needs, documents, and linked shifts.',
        doThis: 'Keep contact and care needs summary current for staff briefing.',
        mistakes: 'Using service user name as billing client — billing is always an Organisation.',
      },
    ],
    relatedLinks: [{ label: 'Shifts', href: '/portal/shifts' }, { label: 'Care locations', href: '/portal/care-locations' }],
  }),

  shifts: page({
    title: 'Shifts',
    tagline: 'Rota, cover, attendance, and the pay/bill pipeline',
    overview:
      'Create shifts, assign workers, review clock-in/out, approve hours, then pass records to timesheets (pay) and invoices (bill).',
    whenToUse: 'Daily rota work, approving attendance after shifts, and troubleshooting pay or invoice issues.',
    quickSteps: [
      'Create shift → set billing client (organisation) + care location + role.',
      'Assign worker → worker accepts and clocks on phone.',
      'Approve attendance in Review Queue → appears in Timesheet Ready.',
      'Generate timesheet (pay) separately from client invoice (bill).',
    ],
    sections: [
      {
        id: 'operations',
        title: 'Shift Operations',
        what: 'Tabs: Needs action, Upcoming, History, All — your live rota cockpit.',
        doThis: 'Needs action = unassigned, rejected, or incomplete. Resolve before pay week ends.',
        mistakes: 'Completing a shift without approved attendance — pay and bill may stall.',
      },
      {
        id: 'applications',
        title: 'Shift applications',
        what: 'Workers who tapped “I’m interested” on open shifts.',
        doThis: 'Review and confirm assignment — you still control the final assign.',
        mistakes: 'Assuming interest = assigned — worker must be formally assigned.',
      },
      {
        id: 'attendance-review',
        title: 'Attendance Review Queue',
        what: 'Submitted clock records waiting for your decision.',
        doThis: 'Check payable times, GPS, early/late flags → Approve, Approve with warning, or Reject.',
        mistakes: 'Approving same-minute clock-in/out — worker gets 0 payable hours (invoice may still bill scheduled hours).',
      },
      {
        id: 'on-hold',
        title: 'Attendance on hold',
        what: 'Rejected records needing correction or investigation.',
        doThis: 'Admin-correct if needed, or worker re-clocks after rejection.',
        mistakes: 'Leaving records on hold past pay day — they will not reach timesheet.',
      },
      {
        id: 'pending-timesheet',
        title: 'Pending timesheet',
        what: 'Approved records not yet on any timesheet.',
        doThis: 'Click Go to Timesheets → generate for the pay week covering clock-out dates.',
        mistakes: 'Waiting for payroll before timesheet — order is always timesheet first.',
      },
      {
        id: 'timesheet-ready',
        title: 'Approved / Timesheet ready',
        what: 'Approved attendance moving through pay and bill stages.',
        doThis: 'Export CSV when auditing. Badges show On timesheet, Invoiced, Processed.',
        mistakes: 'Editing approved rows after lock — unlock timesheet first or void and regenerate.',
      },
      {
        id: 'archive',
        title: 'Processed / Archive',
        what: 'Financially closed records (payroll + client billing complete). Read-only.',
        doThis: 'Use for history and audits only — no edits here.',
        mistakes: 'Expecting to change archived rows — create adjustment in a new period instead.',
      },
    ],
    workflows: [
      { id: 'payroll', label: 'Weekly pay workflow', tourId: TOUR_IDS.payroll, description: 'Approve → timesheet → lock → payroll.' },
      { id: 'billing', label: 'Client billing', description: 'Ensure billing client on shift → Invoices page → generate → send.' },
    ],
    glossary: glossaryCommon,
    relatedLinks: [
      { label: 'Timesheets', href: '/portal/timesheets' },
      { label: 'Invoices', href: '/portal/invoices' },
      { label: 'Organisations', href: '/portal/organisations' },
    ],
  }),

  timesheets: page({
    title: 'Timesheets',
    tagline: 'Bundle approved hours for worker pay (part of the predictable financial pipeline)',
    overview:
      'Turn approved attendance into weekly pay documents — one per worker per pay period. Lock before payroll. This is the bridge between Shifts approval and both worker payroll and client invoicing. Thanks to role dropdown standardization, charge rate mismatches from Organisations are now prevented at the source.',
    whenToUse: 'After approving attendance, before running payroll. Typically once per pay week.',
    quickSteps: [
      'Set pay period dates (use “last week” preset or fit to approvals).',
      'Generate drafts — one timesheet per worker with eligible shifts.',
      'Review hours → Lock → Export CSV for payroll software or PDF for records.',
    ],
    sections: [
      {
        id: 'pay-period',
        title: 'Pay period',
        what: 'Start/end datetime window — only attendance with clock-out in range is included.',
        doThis: 'Widen end date if shifts approved today are missing.',
        mistakes: 'Period ending before the shift clock-out — generates zero rows.',
      },
      {
        id: 'generate',
        title: 'Generate',
        what: 'Creates draft timesheets from approved, uninvoiced attendance without a timesheet.',
        doThis: 'Check preview counts before generating.',
        mistakes: 'Double pay — void old draft if worker already on a timesheet for same shifts.',
      },
      {
        id: 'lock-export',
        title: 'Lock & export',
        what: 'Lock freezes lines; export CSV for payroll software or PDF for filing and audits.',
        doThis: 'Lock only when hours are correct. Locked timesheets feed Payroll page.',
        mistakes: 'Unlocking after payroll export — coordinate with finance first.',
      },
    ],
    workflows: [{ id: 'payroll', label: 'Weekly pay tour', tourId: TOUR_IDS.payroll }],
    glossary: glossaryCommon.slice(2, 4),
    relatedLinks: [{ label: 'Shifts', href: '/portal/shifts' }, { label: 'Payroll', href: '/portal/payroll' }],
  }),

  payroll: page({
    title: 'Payroll',
    tagline: 'Gross pay from locked timesheets (admin only) — end of the worker side of the predictable pipeline',
    overview:
      'Build pay runs from locked/exported timesheets. Uses worker pay rates — never client charge rates. Comes after Timesheets lock; parallel to client invoicing. Role standardization in upstream forms (Shifts, charge rates) now makes this step far more reliable.',
    whenToUse: 'After timesheets are locked for the pay week. Admin accounts only.',
    quickSteps: [
      'Select pay period → see eligible locked timesheets.',
      'Generate payroll batch → review gross per worker.',
      'Lock batch → export CSV to your payroll provider or PDF for audit records.',
    ],
    sections: [
      {
        id: 'eligible',
        title: 'Eligible timesheets',
        what: 'Locked timesheets not yet on a payroll batch.',
        doThis: 'Fix missing pay rates on employee profile or Settings default rate.',
        mistakes: '£0 lines — usually 0 payable hours on attendance (same clock in/out).',
      },
      {
        id: 'batch',
        title: 'Payroll batch',
        what: 'Grouped pay run with total gross.',
        doThis: 'Lock when ready; export CSV or PDF once locked.',
        mistakes: 'Including draft timesheets — only locked/exported qualify.',
      },
    ],
    workflows: [{ id: 'payroll', label: 'Weekly pay tour', tourId: TOUR_IDS.payroll }],
    adminOnly: true,
    relatedLinks: [{ label: 'Timesheets', href: '/portal/timesheets' }, { label: 'Settings', href: '/portal/settings' }],
  }),

  invoices: page({
    title: 'Invoices',
    tagline: 'Bill clients for scheduled shift hours (admin only) — the client side of the predictable financial pipeline',
    overview:
      'Generate UK-style tax invoices per organisation from approved shifts with a billing client. Bills scheduled hours at charge rates. Parallel to payroll; reaches "Financially Closed" when both complete. Role dropdowns in Organisations and Shifts now protect against the most common failure mode (mismatched roles).',
    whenToUse: 'End of billing period, after shifts are approved. Separate from worker payroll.',
    quickSteps: [
      'Pick organisation (billing client) and invoice period.',
      'Preview eligible shifts — fix missing billing client on Shifts if count is 0.',
      'Generate draft → PDF → Send to client email → Mark paid when received.',
    ],
    sections: [
      {
        id: 'organisation',
        title: 'Billing organisation',
        what: 'The council or agency paying you — must match shift billing client.',
        doThis: 'Set charge rates under Organisations before generating.',
        mistakes: 'Wrong period end — invoice filters by clock-out in range, not approval date.',
      },
      {
        id: 'preview',
        title: 'Eligible shifts preview',
        what: 'Diagnostics for missing billing client, charge rate, or wrong dates.',
        doThis: 'Read warning lines; patch shifts via billing client field.',
        mistakes: 'Expecting invoice to use clock hours — it uses scheduled shift length.',
      },
      {
        id: 'send',
        title: 'Send & paid',
        what: 'Send emails PDF to organisation contact. Mark paid closes the billing loop.',
        doThis: 'Send triggers invoiced flag; paid + payroll completes archive.',
        mistakes: 'Leaving draft forever — sent status moves shifts toward Processed/archive.',
      },
    ],
    adminOnly: true,
    glossary: glossaryCommon,
    relatedLinks: [
      { label: 'Organisations', href: '/portal/organisations' },
      { label: 'Shifts', href: '/portal/shifts' },
      { label: 'Accounts', href: '/portal/accounts' },
    ],
  }),

  accounts: page({
    title: 'Accounts',
    tagline: 'Month view across pay and bill (admin only)',
    overview:
      'Read-only summary: hours awaiting timesheet, locked sheets ready for payroll, batches, and invoice totals.',
    whenToUse: 'Month-end review, management reporting, or quick pipeline health check.',
    quickSteps: [
      'Pick month at top.',
      'Compare awaiting timesheet vs locked vs payroll batches.',
      'Use quick links to drill into Timesheets, Payroll, or Invoices.',
    ],
    sections: [
      {
        id: 'awaiting',
        title: 'Awaiting timesheet',
        what: 'Approved attendance in month not yet on a timesheet.',
        doThis: 'If high, open Timesheets and generate for that period.',
        mistakes: 'Ignoring because payroll “looks done” — check this card first.',
      },
      {
        id: 'outstanding',
        title: 'Outstanding invoices',
        what: 'Sent but not paid invoice total for the month.',
        doThis: 'Chase clients from organisation contact details.',
        mistakes: 'Confusing with draft invoices — only sent counts as outstanding.',
      },
    ],
    adminOnly: true,
    relatedLinks: [
      { label: 'Timesheets', href: '/portal/timesheets' },
      { label: 'Invoices', href: '/portal/invoices' },
    ],
  }),

  'care-locations': page({
    title: 'Care locations',
    tagline: 'Sites, homes, and clock-in QR codes',
    overview: 'Register places where care is delivered. Link to shifts and optional GPS/QR verification.',
    whenToUse: 'New contract site, QR setup for worker clock-in, or location pickers on shifts.',
    quickSteps: ['Add location with address.', 'Generate QR if using site verification.', 'Select location when creating shifts.'],
    sections: [
      {
        id: 'qr',
        title: 'QR codes',
        what: 'Workers scan to prove they are on site before clock-in.',
        doThis: 'Print and post at reception; test with a worker phone.',
        mistakes: 'Expecting GPS alone — desktop clocks may show “no GPS”.',
      },
    ],
    relatedLinks: [{ label: 'Shifts', href: '/portal/shifts' }],
  }),

  organisations: page({
    title: 'Organisations',
    tagline: 'Councils, agencies, and billing clients — the foundation of your revenue pipeline',
    overview:
      'Commissioning bodies (councils, care homes, agencies) you supply staff to. This is where you hold contact details for invoicing, set the charge rates that feed the predictable financial pipeline (Shifts → Timesheets → Invoices), and link to staffing requests. Accurate rates here ensure every approved shift bills correctly — directly supporting CQC financial governance and contract compliance.',
    whenToUse: 'New council contract, setting or updating invoice rates before generating invoices, fixing “no billing client” errors on shifts, or reviewing client billing health.',
    quickSteps: [
      'Create organisation with accurate invoice contact email (this is where PDFs are sent).',
      'Add charge rates per role (e.g. Healthcare Assistant £/hr) — these must exactly match the role names used on shifts for that client.',
      'Set the organisation as the billing client on shifts (or via staffing request convert) so hours flow into the pipeline.',
      'Use “Manage rates” from the table to version rates over time (effective dates supported).',
    ],
    sections: [
      {
        id: 'charge-rates',
        title: 'Billing rates (Charge rates)',
        what: 'The rates you charge the client per role/hour. Separate from worker pay rates (set in Employees or Settings). These power client invoices and must match shift roles exactly for the financial pipeline to close cleanly.',
        doThis: 'Always set rates before the first invoice for a new client. Use the effective_from date for rate changes. Role names are case-sensitive and must match Shifts exactly.',
        mistakes: 'Mismatching role names (e.g. “Care Assistant” vs “Healthcare Assistant”) — invoices will fail or use wrong rates. Never edit worker pay here expecting client bills to change.',
      },
      {
        id: 'pipeline',
        title: 'How Organisations fit the predictable financial pipeline',
        what: 'Shifts (with billing client set) → Approved attendance → Timesheet → Payroll (worker pay) + parallel Invoice (using charge rates here) → Financially Closed.',
        doThis: 'After setting rates, go to Shifts → approve attendance → Timesheets → Invoices. The “Financially Closed” badges will appear when both sides complete.',
        mistakes: 'Generating invoices without rates set — the system will block and show clear diagnostics.',
      },
    ],
    workflows: [
      { id: 'billing', label: 'End-to-end client billing', description: 'Create org → set charge rates → link to shifts → approve attendance → generate invoice.' },
    ],
    glossary: [
      ...glossaryCommon,
      {
        term: 'Charge rate',
        definition: 'What the client organisation pays you. Distinct from worker pay rate. Set here; used only for client invoices.',
      },
    ],
    relatedLinks: [
      { label: 'Invoices — generate and send using these rates', href: '/portal/invoices' },
      { label: 'Shifts — set billing client and drive the pipeline', href: '/portal/shifts' },
      { label: 'Staffing requests — convert directly to billed shifts', href: '/portal/staffing-requests' },
    ],
    tips: [
      'Role names must match exactly between here and the shifts you create for this client — this is the most common cause of invoice blocks.',
      'Use the help bubble on this page and the “Billing rates vs worker pay” box for non-technical staff. Clear separation here prevents pay disputes later.',
      'For future client self-service views (in development), this data will power what councils/care homes see about their supplied staff and costs.',
    ],
  }),

  'staffing-requests': page({
    title: 'Staffing requests',
    tagline: 'Cover requests from councils and partners',
    overview: 'Inbound requests for staff cover. Convert to shifts when you can fulfil — links billing organisation.',
    whenToUse: 'Council emails or portal requests for workers at a location.',
    quickSteps: [
      'Open request → verify organisation and role.',
      'Convert to shift — sets billing client automatically when configured.',
      'Assign worker on Shifts page.',
    ],
    sections: [
      {
        id: 'convert',
        title: 'Convert to shift',
        what: 'Creates a shift pre-linked to organisation and requirements.',
        doThis: 'Confirm quantity if multiple shifts needed.',
        mistakes: 'Converting for unverified org without checking contract terms.',
      },
    ],
    relatedLinks: [{ label: 'Shifts', href: '/portal/shifts' }, { label: 'Organisations', href: '/portal/organisations' }],
  }),

  'care-enquiries': page({
    title: 'Care enquiries',
    tagline: 'New care leads from website or office',
    overview: 'Triaged enquiries from people seeking care. Move through status until they become service users or are closed.',
    whenToUse: 'When a new domiciliary or care home enquiry arrives.',
    quickSteps: ['Review enquiry details.', 'Contact family or commissioner.', 'Convert or link to service user when engaged.'],
    relatedLinks: [{ label: 'Service users', href: '/portal/service-users' }],
  }),

  'lead-discovery': page({
    title: 'Find Leads',
    tagline: 'Discover care homes and councils that need staffing',
    overview:
      'Search the CQC register by postcode, council, or care group to find organisations you can supply workers to. This is the top of your staffing pipeline — leads you save here flow into Sales CRM for outreach.',
    whenToUse: 'Prospecting new contracts, expanding into a new area, or refreshing your target list before a sales push.',
    quickSteps: [
      'Enter a postcode or pick a local authority — results load from the live CQC register.',
      'Review ratings, bed count, and contact details; save promising homes as leads.',
      'Open Sales CRM to email or call saved leads — replies land in Portal Inbox (Staffing tab).',
    ],
    nextAction: { label: 'Open Sales CRM', href: '/portal/sales-leads' },
    sections: [
      {
        id: 'search',
        title: 'Search & filters',
        what: 'Postcode radius, council, and partner filters to narrow thousands of homes to your patch.',
        doThis: 'Start broad (council), then refine by rating or service type. Save anything worth a conversation.',
        mistakes: 'Expecting phone numbers on every record — use CRM to capture contacts as you research.',
      },
      {
        id: 'pipeline',
        title: 'Into the sales pipeline',
        what: 'Saved leads become CRM contacts with status tracking through to signed client.',
        doThis: 'After saving, switch to Sales CRM and move leads through Called → Interested → Client.',
        mistakes: 'Collecting leads but never following up — inbox and CRM are linked for a reason.',
      },
    ],
    relatedLinks: [
      { label: 'Sales CRM', href: '/portal/sales-leads' },
      { label: 'Portal Inbox (staffing)', href: '/portal/inbox?department=staffing' },
      { label: 'Client organisations', href: '/portal/organisations' },
    ],
    tips: [
      'This page is under Staffing & Sales in the menu — your sales team should live here and in CRM.',
      'When CQC data looks stale, try a slightly wider postcode — the register updates on their schedule.',
    ],
  }),

  'sales-leads': page({
    title: 'Sales CRM',
    tagline: 'Track outreach from first call to signed client',
    overview:
      'Your staffing sales pipeline. Manage lead status, log calls, send branded emails from staffing@osabeacares.co.uk, and convert won deals into client organisations. Replies from care homes appear in Portal Inbox.',
    whenToUse: 'Daily sales work — following up Find Leads saves, logging conversations, sending proposals, and marking clients ready for staffing requests.',
    quickSteps: [
      'Filter by status (New, Contacted, Interested, Client) to focus today’s calls.',
      'Open a lead → update status and notes after every touchpoint.',
      'Send email from the composer — it is branded and threads into Portal Inbox automatically.',
      'When they sign, mark as Client and create/link an Organisation for billing.',
    ],
    nextAction: { label: 'Check staffing inbox', href: '/portal/inbox?department=staffing' },
    sections: [
      {
        id: 'status',
        title: 'Lead status',
        what: 'Stages from prospect through to active billing client.',
        doThis: 'Move status only when something real happened — auditors and your future self rely on this trail.',
        mistakes: 'Jumping straight to Client without an organisation record — billing will break later.',
      },
      {
        id: 'email',
        title: 'Email outreach',
        what: 'Sends from staffing@ with your logo; inbound replies sync to Portal Inbox.',
        doThis: 'Use templates for consistency; personalise the first line. Check inbox within the same session.',
        mistakes: 'Replying from personal Gmail — breaks the thread and loses audit trail.',
      },
    ],
    relatedLinks: [
      { label: 'Portal Inbox', href: '/portal/inbox?department=staffing' },
      { label: 'Find Leads', href: '/portal/lead-discovery' },
      { label: 'Organisations', href: '/portal/organisations' },
    ],
  }),

  'portal-inbox': page({
    title: 'Portal Inbox',
    tagline: 'Staffing, recruitment, and general email — in one place',
    overview:
      'Your free in-app inbox for @osabeacares.co.uk. Staffing replies from Sales CRM land here; recruitment and info@ enquiries have their own tabs. No separate mailboxes to pay for — the portal is the inbox.',
    whenToUse: 'Whenever you send outreach from CRM or recruitment, or when families email info@. Check daily so nothing waits overnight.',
    quickSteps: [
      'Pick the department tab: Staffing | Recruitment | General — each has its own address.',
      'Open a thread → read the branded message → reply from here (stays in the thread).',
      'Use filters (Open / Closed) to clear your queue before end of day.',
      'From a lead in CRM, jump straight to their thread via the inbox link on the contact.',
    ],
    nextAction: { label: 'Back to Sales CRM', href: '/portal/sales-leads' },
    sections: [
      {
        id: 'staffing',
        title: 'Staffing tab',
        what: 'staffing@osabeacares.co.uk — outreach replies and client conversations.',
        doThis: 'Tie each thread to the CRM contact; close when resolved.',
        mistakes: 'Leaving threads open after the deal is won — mark closed for a clean queue.',
      },
      {
        id: 'recruitment',
        title: 'Recruitment tab',
        what: 'recruitment@ — applicant questions and job enquiries.',
        doThis: 'Link to Recruitment pipeline when someone applies.',
        mistakes: 'Mixing applicant mail with staffing — use the correct tab.',
      },
      {
        id: 'general',
        title: 'General tab',
        what: 'info@ — website contact form and general enquiries.',
        doThis: 'Route care enquiries to Care Enquiries page; staffing to CRM.',
        mistakes: 'Ignoring info@ because it is not your tab — assign owners per department.',
      },
    ],
    relatedLinks: [
      { label: 'Sales CRM', href: '/portal/sales-leads' },
      { label: 'Recruitment', href: '/portal/recruitment' },
      { label: 'Website enquiries', href: '/portal/contact-submissions' },
    ],
    tips: [
      'Emails you send from CRM appear here automatically — you should never need to leave the portal.',
      'If a thread looks empty, refresh once; sync is near-real-time from Resend inbound.',
    ],
  }),

  'contact-submissions': page({
    title: 'Website enquiries',
    tagline: 'Messages from the public website contact form',
    overview: 'Form submissions from osabeacares.co.uk. Triage here; serious care requests should move to Care Enquiries. General questions may also appear in Portal Inbox (General tab).',
    whenToUse: 'When marketing or info@ forwards a web form, or during daily enquiries review.',
    quickSteps: [
      'Read submission and note contact details.',
      'Reply via Portal Inbox (General) or phone as appropriate.',
      'Create a care enquiry if they need a package of care.',
    ],
    nextAction: { label: 'Portal Inbox (General)', href: '/portal/inbox?department=info' },
    relatedLinks: [
      { label: 'Care enquiries', href: '/portal/care-enquiries' },
      { label: 'Portal Inbox', href: '/portal/inbox?department=info' },
    ],
  }),

  support: page({
    title: 'Support',
    tagline: 'Internal help requests and technical issues',
    overview: 'Log and track support tickets from staff or portal users. Use for system issues, access problems, or process questions that need a response.',
    whenToUse: 'When someone cannot access a feature, finds a bug, or needs hands-on help beyond the Portal Help bubble.',
    quickSteps: [
      'Create a ticket with clear steps to reproduce.',
      'Assign priority — blocking payroll or safeguarding gets urgent.',
      'Close with a note so the next person has context.',
    ],
    relatedLinks: [{ label: 'Settings', href: '/portal/settings' }],
  }),

  'compliance-centre': page({
    title: 'Compliance Hub',
    tagline: 'Policies, incidents, complaints, CQC insights',
    overview: 'Central compliance workspace: policies, incidents, complaints, and inspection-oriented dashboards.',
    whenToUse: 'Policy updates, logging incidents, complaint handling, pre-CQC preparation.',
    quickSteps: ['Pick the tab you need (Policies, Incidents, Complaints, Insights).', 'Log events promptly with factual detail.', 'Export or use Audit for evidence trails.'],
    sections: [
      { id: 'policies', title: 'Policies tab', what: 'Master policy list and assignments.', doThis: 'Assign policies to roles; track acknowledgements.', mistakes: 'Stale PDF versions — upload new issue when CQC standards change.' },
      { id: 'incidents', title: 'Incidents tab', what: 'Safeguarding and operational incidents.', doThis: 'Record near-misses and full incidents; link staff/service users.', mistakes: 'Closing without root cause — auditors expect learning loop.' },
      { id: 'complaints', title: 'Complaints tab', what: 'Formal complaints register.', doThis: 'Acknowledge, investigate, respond within policy timelines.', mistakes: 'Mixing with feedback — complaints need structured workflow.' },
      { id: 'insights', title: 'Insights tab', what: 'CQC-style indicators and alerts.', doThis: 'Review before inspection mode walkthrough.', mistakes: 'Treating as optional — good scores depend on underlying data quality.' },
    ],
    relatedLinks: [{ label: 'Audit & Inspection', href: '/portal/audit' }, { label: 'Training', href: '/portal/training' }],
  }),

  feedback: page({
    title: 'Feedback',
    tagline: 'Service user and family voice',
    overview: 'Compliments and concerns logged for governance, quality improvement, and CQC “well-led” evidence.',
    whenToUse: 'When receiving verbal or written feedback you need on record.',
    quickSteps: ['Log feedback with service user link if possible.', 'Escalate serious concerns to Complaints or Incidents.'],
    relatedLinks: [{ label: 'Compliance Hub', href: '/portal/compliance-centre' }],
  }),

  training: page({
    title: 'Training',
    tagline: 'Mandatory courses, matrix, and expiry',
    overview: 'Assign training, track completion, verify certificates, and spot expiring qualifications before they block shifts.',
    whenToUse: 'Onboarding, renewals, audit prep, and when assignment compliance fails.',
    quickSteps: ['Open training matrix for overview.', 'Request or assign courses to individuals.', 'Verify uploads from worker portal.'],
    sections: [
      {
        id: 'matrix',
        title: 'Training matrix',
        what: 'Grid of who has what training and expiry status.',
        doThis: 'Filter expiring soon; chase workers via portal.',
        mistakes: 'Manual spreadsheets alongside — matrix is the system of record.',
      },
    ],
    relatedLinks: [{ label: 'Employees', href: '/portal/employees' }],
  }),

  'dbs-register': page({
    title: 'DBS register',
    tagline: 'Criminal record check register',
    overview: 'Track Enhanced DBS status, update service subscription, and prove safer recruitment at inspection.',
    whenToUse: 'New hires, annual review, and when DBS blocks assignment.',
    quickSteps: ['Ensure DBS proof on employee profile.', 'Record check date and level.', 'Monitor update service where applicable.'],
    relatedLinks: [{ label: 'Recruitment', href: '/portal/recruitment' }],
  }),

  audit: page({
    title: 'Audit & inspection',
    tagline: 'Activity trail and inspection mode',
    overview: 'Every significant action is logged. Use with Inspection Mode for controlled CQC walkthroughs.',
    whenToUse: 'Pre-inspection, investigating who changed what, or auditor review.',
    quickSteps: ['Filter audit log by date/user/action.', 'Enable Inspection Mode from header for read-only tour.', 'Export packs if available.'],
    sections: [
      {
        id: 'inspection-mode',
        title: 'Inspection mode',
        what: 'Limits destructive actions for inspector sessions.',
        doThis: 'Enable before visitor arrives; disable after.',
        mistakes: 'Inspectors editing live data without mode — use bannered mode instead.',
      },
    ],
    workflows: [{ id: 'overview', label: 'Auditor portal tour', tourId: TOUR_IDS.overview }],
    relatedLinks: [{ label: 'Compliance Hub', href: '/portal/compliance-centre' }],
  }),

  'admin-users': page({
    title: 'Team Access & Permissions',
    tagline: 'Who can access this workspace and how their navigation is prioritised',
    overview:
      'Invite staff through the secure invitation flow. Role is the permission authority. Primary work area only prioritises navigation and dashboard links; it never grants access beyond the assigned role.',
    whenToUse: 'New office hire, role change, assigning a primary work area, or removing leaver access.',
    quickSteps: [
      'Pick the staff role allowed by the current subscription plan.',
      'Set Primary work area when someone mainly works in management, operations, recruitment, compliance, HR, finance, clients, or support.',
      'Use Organisation-wide when navigation should not be visually prioritised to one area.',
    ],
    mistakes: 'Treating work area as a permission grant. Sensitive routes must remain controlled by role and workspace membership.',
  }),

  'plans-billing': page({
    title: 'Plans & Billing',
    tagline: 'Subscription status and Stripe-hosted billing controls',
    overview:
      'Workspace Owners can review the current plan, compare plan limits, start Stripe-hosted Checkout, or open the Stripe Customer Portal. CareOps only applies paid entitlements after verified Stripe webhook state is received.',
    whenToUse: 'Plan purchase, payment method update, invoice download, cancellation review, or payment attention follow-up.',
    quickSteps: [
      'Review current subscription status and included usage limits.',
      'Choose a configured Stripe-hosted plan option when billing is available.',
      'Use Manage billing for invoices, payment methods, cancellation, or Stripe-hosted plan changes.',
    ],
    mistakes: 'Treating a browser Checkout return as confirmation. Wait for verified Stripe webhook projection before saying a subscription is confirmed.',
  }),

  settings: page({
    title: 'Settings',
    tagline: 'Organisation-wide configuration',
    overview: 'Company details, handbook, default worker pay rate, supplier fields for invoices, and portal preferences.',
    whenToUse: 'Branding, payroll defaults, invoice letterhead, or org legal details.',
    quickSteps: [
      'Organisation tab — legal name, address (appears on invoices).',
      'Default hourly rate — worker pay fallback, not client charge rate.',
      'Supplier fields — your details on PDF invoices.',
    ],
    sections: [
      {
        id: 'pay-vs-charge',
        title: 'Pay vs charge',
        what: 'Settings default rate = worker pay. Client billing = Organisations charge rates.',
        doThis: 'Configure both sides explicitly.',
        mistakes: 'One rate for everything — causes £0 invoice or wrong margin.',
      },
    ],
    glossary: glossaryCommon,
  }),

  templates: page({
    title: 'Generated staff forms',
    tagline: 'Employee record documents',
    overview: 'Generate tenant-scoped documents from employee and applicant records.',
    whenToUse: 'Use this only for legacy generated forms. Normal onboarding forms live inside the person profile.',
    relatedLinks: [{ label: 'Employee records', href: '/portal/employees' }],
  }),

  'template-library': page({
    title: 'Template library',
    tagline: 'Document template catalogue',
    overview: 'Browse and manage document templates for contracts, letters, and compliance packs.',
    whenToUse: 'HR document standardisation or CQC evidence packs.',
  }),

  'form-editor': page({
    title: 'Form editor',
    tagline: 'Edit a specific form',
    overview: 'Design or update fields for a single form used in recruitment or worker onboarding.',
    whenToUse: 'When compliance team needs to change questions on an active form.',
    mistakes: 'Editing live forms mid-cohort — prefer versioning for audit trail.',
  }),

  default: page({
    title: 'Portal help',
    tagline: 'Guidance for this area',
    overview: 'Use the Help bubble on any page for what you are looking at and what to do next.',
    whenToUse: 'Whenever you are unsure — content updates per screen.',
    workflows: [{ id: 'overview', label: 'Full portal tour', tourId: TOUR_IDS.overview }],
  }),
};

const COMPLIANCE_TAB_HELP = {
  incidents: 'compliance-incidents',
  complaints: 'compliance-complaints',
  insights: 'compliance-insights',
  policies: 'compliance-centre',
};

PORTAL_PAGE_HELP['compliance-incidents'] = page({
  ...PORTAL_PAGE_HELP['compliance-centre'],
  title: 'Compliance Hub — Incidents',
  tagline: 'Log and investigate incidents',
  overview: 'Record safeguarding and operational incidents with structured follow-up for CQC.',
  whenToUse: 'Immediately when an incident occurs or is reported.',
});

PORTAL_PAGE_HELP['compliance-complaints'] = page({
  ...PORTAL_PAGE_HELP['compliance-centre'],
  title: 'Compliance Hub — Complaints',
  tagline: 'Formal complaints register',
  overview: 'Manage complaints with acknowledgement, investigation, and outcome tracking.',
  whenToUse: 'When someone makes a formal complaint (not casual feedback).',
});

PORTAL_PAGE_HELP['compliance-insights'] = page({
  ...PORTAL_PAGE_HELP['compliance-centre'],
  title: 'Compliance Hub — Insights',
  tagline: 'CQC-oriented dashboards',
  overview: 'Indicators and alerts summarising compliance posture before inspections.',
  whenToUse: 'Weekly management review or pre-inspection prep.',
});

PORTAL_PAGE_HELP['portal-inbox-staffing'] = page({
  ...PORTAL_PAGE_HELP['portal-inbox'],
  title: 'Portal Inbox — Staffing',
  tagline: 'Replies to staffing@ and sales outreach',
  nextAction: { label: 'Open Sales CRM', href: '/portal/sales-leads' },
});

PORTAL_PAGE_HELP['portal-inbox-recruitment'] = page({
  ...PORTAL_PAGE_HELP['portal-inbox'],
  title: 'Portal Inbox — Recruitment',
  tagline: 'Applicant and job enquiries at recruitment@',
  nextAction: { label: 'Open Recruitment', href: '/portal/recruitment' },
});

PORTAL_PAGE_HELP['portal-inbox-info'] = page({
  ...PORTAL_PAGE_HELP['portal-inbox'],
  title: 'Portal Inbox — General',
  tagline: 'info@ and website contact messages',
  nextAction: { label: 'Website enquiries', href: '/portal/contact-submissions' },
});

/**
 * Resolve help config from current route.
 */
export function resolvePageHelpId(pathname, search = '') {
  const path = (pathname || '').replace(/\/$/, '') || '/portal/dashboard';
  const params = new URLSearchParams(search || '');
  const tab = params.get('tab');

  if (path.includes('/portal/compliance-centre') && tab && COMPLIANCE_TAB_HELP[tab]) {
    return COMPLIANCE_TAB_HELP[tab];
  }

  if (path.includes('/portal/inbox')) {
    const dept = params.get('department') || 'staffing';
    const inboxByDept = {
      staffing: 'portal-inbox-staffing',
      recruitment: 'portal-inbox-recruitment',
      info: 'portal-inbox-info',
      general: 'portal-inbox-info',
    };
    return inboxByDept[dept] || 'portal-inbox';
  }

  const rules = [
    { prefix: '/portal/dashboard', id: 'dashboard' },
    { prefix: '/portal/employees', id: 'employees' },
    { prefix: '/portal/recruitment', id: 'recruitment' },
    { prefix: '/portal/service-users', id: 'service-users' },
    { prefix: '/portal/shifts', id: 'shifts' },
    { prefix: '/portal/timesheets', id: 'timesheets' },
    { prefix: '/portal/payroll', id: 'payroll' },
    { prefix: '/portal/invoices', id: 'invoices' },
    { prefix: '/portal/accounts', id: 'accounts' },
    { prefix: '/portal/care-locations', id: 'care-locations' },
    { prefix: '/portal/organisations', id: 'organisations' },
    { prefix: '/portal/staffing-requests', id: 'staffing-requests' },
    { prefix: '/portal/lead-discovery', id: 'lead-discovery' },
    { prefix: '/portal/sales-leads', id: 'sales-leads' },
    { prefix: '/portal/contact-submissions', id: 'contact-submissions' },
    { prefix: '/portal/support', id: 'support' },
    { prefix: '/portal/care-enquiries', id: 'care-enquiries' },
    { prefix: '/portal/compliance-centre', id: 'compliance-centre' },
    { prefix: '/portal/feedback', id: 'feedback' },
    { prefix: '/portal/training', id: 'training' },
    { prefix: '/portal/dbs-register', id: 'dbs-register' },
    { prefix: '/portal/audit', id: 'audit' },
    { prefix: '/portal/admin-users', id: 'admin-users' },
    { prefix: '/portal/plans-billing', id: 'plans-billing' },
    { prefix: '/portal/settings', id: 'settings' },
    { prefix: '/portal/templates', id: 'templates' },
    { prefix: '/portal/template-library', id: 'template-library' },
    { prefix: '/portal/forms', id: 'form-editor' },
  ];

  if (path === '/portal' || path === '/portal/index') return 'dashboard';

  for (const { prefix, id } of rules) {
    if (path === prefix || path.startsWith(`${prefix}/`)) return id;
  }

  return 'default';
}

export function getPageHelp(pathname, search = '') {
  const id = resolvePageHelpId(pathname, search);
  return PORTAL_PAGE_HELP[id] || PORTAL_PAGE_HELP.default;
}

export function getSectionHelp(pathname, search, sectionId) {
  const help = getPageHelp(pathname, search);
  if (!help?.sections?.length || !sectionId) return null;
  return help.sections.find((s) => s.id === sectionId) || null;
}
